export interface User {
  atomicPermissions: AtomicPermissions;
  canAdmin: boolean;
  accountStatus: 'active' | 'paused' | 'deleted';
  profileVersion: number;
  jobTitle: string;
  bio: string;
  timeZone: string | null;
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'member';
  createdAt: string;
}

export interface AtomicPermissions {
  enabled: boolean;
  read: boolean;
  draft: boolean;
  edit: boolean;
  manage: boolean;
}
export const DISABLED_ATOMIC_PERMISSIONS: AtomicPermissions = {
  enabled: false,
  read: false,
  draft: false,
  edit: false,
  manage: false,
};

export interface Workspace {
  id: string;
  name: string;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

export type NewProject = Pick<Project, 'name'> & Partial<Pick<Project, 'description'>>;

export interface RecurrenceConfig {
  frequency: 'daily' | 'weekly' | 'monthly';
  timeZone: string;
  anchorDate: string;
  localTime: string | null;
}
export interface Recurrence extends RecurrenceConfig {
  id: string;
  active: boolean;
  version: number;
  anchorIndex: number;
}
export interface Task {
  version: number;
  recurrence: Recurrence | null;
  occurrenceIndex: number | null;
  id: string;
  title: string;
  description: string;
  status: 'todo' | 'in_progress' | 'done';
  priority: 'low' | 'normal' | 'high';
  assigneeId: string | null;
  projectId: string | null;
  dueDate: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

export interface Channel {
  id: string;
  name: string;
  description: string;
  createdAt: string;
}

export interface Message {
  version: number;
  editedAt: string | null;
  id: string;
  body: string;
  authorId: string;
  channelId: string | null;
  recipientId: string | null;
  createdAt: string;
}

export interface Invite {
  id: string;
  token: string;
  expiresAt: string;
  email: string | null;
}

export type NewTask = { recurrence?: RecurrenceConfig } & Pick<Task, 'title'> &
  Partial<
    Pick<Task, 'description' | 'status' | 'priority' | 'assigneeId' | 'projectId' | 'dueDate'>
  >;
export type TaskPatch = { expectedVersion?: number } & Partial<
  Pick<
    Task,
    'title' | 'description' | 'status' | 'priority' | 'assigneeId' | 'projectId' | 'dueDate'
  >
>;
export interface ApiErrorBody {
  error: { code: string; message: string };
}

export type PersonalItemKind = 'reminder' | 'waiting' | 'note' | 'bookmark';
export type FollowUpSource = { kind: 'task' | 'channel' | 'dm'; id: string };
export interface PersonalItem {
  source: FollowUpSource | null;
  recurrence: Recurrence | null;
  occurrenceIndex: number | null;
  id: string;
  kind: PersonalItemKind;
  title: string;
  body: string;
  url: string | null;
  counterparty: string | null;
  scheduledAt: string | null;
  timeZone: string;
  completedAt: string | null;
  projectId: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}
export type NewPersonalItem = { recurrence?: RecurrenceConfig } & Pick<
  PersonalItem,
  'kind' | 'title'
> &
  Partial<
    Pick<
      PersonalItem,
      'body' | 'url' | 'counterparty' | 'scheduledAt' | 'timeZone' | 'projectId' | 'source'
    >
  >;
export type PersonalItemPatch = { expectedVersion: number; completed?: boolean } & Partial<
  Omit<NewPersonalItem, 'kind' | 'recurrence'>
>;
export interface Agenda {
  tasks: Task[];
  items: PersonalItem[];
  now: string;
  date: string;
  timeZone: string;
}
export interface SearchResults {
  tasks: Task[];
  projects: Project[];
  items: PersonalItem[];
  truncated: boolean;
}

export interface UnreadCounts {
  channels: Record<string, number>;
  people: Record<string, number>;
}

export interface Upcoming {
  tasks: Task[];
  items: PersonalItem[];
  now: string;
  timeZone: string;
  days: 7 | 30;
  fromDate: string;
  throughDate: string;
  projectId: string | null;
}

export interface NotificationPreferences {
  version: number;
  enabled: boolean;
  timeZone: string;
  dueAlerts: boolean;
  advanceAlerts: boolean;
  advanceMinutes: 60 | 1440;
  morningDigest: boolean;
  morningTime: string;
}
export type NotificationPreferencesPatch = { expectedVersion: number } & Partial<
  Omit<NotificationPreferences, 'version'>
>;
export interface NotificationSubscription {
  id: string;
  label: string;
  createdAt: string;
}
export interface Notifications {
  available: boolean;
  publicKey: string | null;
  preferences: NotificationPreferences;
  subscriptions: NotificationSubscription[];
}
export interface MorningDigest {
  id: string;
  generatedAt: string;
  date: string;
  timeZone: string;
  overdueTasks: number;
  todayTasks: number;
  reminders: number;
  waiting: number;
}

export interface TaskReview {
  taskId: string;
  reviewedAt: string;
  taskVersion: number;
}
export interface Forgotten {
  tasks: Task[];
  reviews: TaskReview[];
  now: string;
  cutoff: string;
  days: 7 | 14 | 30;
  projectId: string | null;
}

export interface AdminAccount {
  atomicPermissions: AtomicPermissions;
  profileVersion: number | null;
  id: string;
  userId: string | null;
  firstName: string;
  lastName: string;
  employeeId: string | null;
  email: string | null;
  displayName: string;
  status: 'pending' | 'active' | 'paused' | 'deleted';
  role: 'admin' | 'member';
  version: number;
  createdAt: string;
  updatedAt: string;
}
